import { useEffect, useState } from "react";
import api, { money, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { escrowLabel } from "@/components/EscrowTimeline";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Euro, Landmark, TrendingUp, Wallet } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grantUser, setGrantUser] = useState(null);
  const [grant, setGrant] = useState({ rebate_discount_pct: "", rebate_credit: "", trade_verified: true });
  const [acting, setActing] = useState(null);

  const load = () => Promise.all([
    api.get("/admin/stats").then((r) => setStats(r.data)),
    api.get("/admin/orders").then((r) => setOrders(r.data)),
    api.get("/admin/users").then((r) => setUsers(r.data)),
    api.get("/admin/trade-applications").then((r) => setApplications(r.data)),
  ]).finally(() => setLoading(false));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const release = async (id) => {
    setActing(id);
    try { await api.post(`/orders/${id}/release`); toast.success("Payout released to the artist."); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setActing(null); }
  };

  const submitGrant = async () => {
    try {
      const payload = {};
      if (grant.rebate_discount_pct !== "") payload.rebate_discount_pct = parseFloat(grant.rebate_discount_pct);
      if (grant.rebate_credit !== "") payload.rebate_credit = parseFloat(grant.rebate_credit);
      payload.trade_verified = !!grant.trade_verified;
      if (grantUser.__appId) {
        await api.post(`/admin/trade-applications/${grantUser.__appId}/approve`, payload);
        toast.success("Application approved & rebate set");
      } else {
        await api.post(`/admin/users/${grantUser.user_id}/rebate`, payload);
        toast.success("Trade rebate granted");
      }
      setGrantUser(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const rejectApp = async (appId) => {
    try { await api.post(`/admin/trade-applications/${appId}/reject`); toast.success("Application rejected"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (loading || !stats) return (<div><Navbar /><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" /></div></div>);

  const statCards = [
    [Euro, "Gross Merchandise Value", money(stats.gmv)],
    [Wallet, "Held (pending delivery)", money(stats.in_escrow)],
    [TrendingUp, "Commission (booked)", money(stats.commission)],
    [Landmark, "Commission released", money(stats.commission_released)],
  ];

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="overline-badge">Admin Control</div>
        <h1 className="font-serif text-4xl mt-2">Platform overview</h1>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {statCards.map(([Icon, l, v]) => (
            <div key={l} className="bg-[var(--bg-dark)] text-white rounded-2xl p-6 relative grain overflow-hidden">
              <Icon className="w-5 h-5 text-[var(--gold)]" />
              <div className="text-xs text-stone-400 mt-3">{l}</div>
              <div className="font-serif text-3xl mt-1">{v}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-6 text-center">
          {[["Orders", stats.orders], ["Paid orders", stats.paid_orders], ["Users", stats.users], ["Artworks", stats.artworks]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-xl border border-[var(--border-subtle)] p-4">
              <div className="font-serif text-2xl">{v}</div><div className="text-xs text-[var(--text-muted)]">{l}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="orders" className="mt-12">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="orders" data-testid="admin-tab-escrow">Order pipeline</TabsTrigger>
            <TabsTrigger value="requests" data-testid="admin-tab-requests">
              Trade requests{applications.length > 0 && <span className="ml-1.5 text-[0.6rem] bg-[var(--gold)] text-white rounded-full px-1.5 py-0.5">{applications.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users & rebates</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-6">
            <div className="bg-white rounded-2xl border border-[var(--border-subtle)] overflow-hidden">
              <div className="grid grid-cols-12 gap-3 px-6 py-3 text-xs font-mono uppercase text-[var(--text-muted)] border-b border-[var(--border-subtle)]">
                <div className="col-span-4">Artwork</div><div className="col-span-2">Amount</div>
                <div className="col-span-2">Commission</div><div className="col-span-2">Status</div><div className="col-span-2 text-right">Action</div>
              </div>
              {orders.length === 0 && <div className="p-6 text-sm text-[var(--text-muted)]">No orders yet.</div>}
              {orders.map((o) => (
                <div key={o.id} className="grid grid-cols-12 gap-3 px-6 py-4 items-center border-b border-[var(--border-subtle)] last:border-0" data-testid={`admin-order-${o.id}`}>
                  <div className="col-span-4"><div className="font-medium text-sm">{o.artwork_title}</div><div className="text-xs text-[var(--text-muted)]">{o.buyer_name} → {o.artist_name}</div></div>
                  <div className="col-span-2 font-mono text-sm">{money(o.amount)}</div>
                  <div className="col-span-2 font-mono text-sm text-[var(--gold-hover)]">{money(o.commission)}</div>
                  <div className="col-span-2"><span className="escrow-badge">{escrowLabel(o.escrow_status)}</span></div>
                  <div className="col-span-2 text-right">
                    {o.escrow_status === "delivered" ? (
                      <button onClick={() => release(o.id)} disabled={acting === o.id} data-testid={`release-btn-${o.id}`}
                        className="btn-gold px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1">
                        {acting === o.id && <Loader2 className="w-3 h-3 animate-spin" />} Release
                      </button>
                    ) : <span className="text-xs text-[var(--text-muted)]">—</span>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="requests" className="mt-6">
            <div className="bg-white rounded-2xl border border-[var(--border-subtle)] overflow-hidden" data-testid="admin-requests-list">
              {applications.length === 0 && <div className="p-6 text-sm text-[var(--text-muted)]">No pending trade applications.</div>}
              {applications.map((a) => (
                <div key={a.id} className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)] last:border-0" data-testid={`admin-application-${a.id}`}>
                  <div>
                    <div className="font-medium text-sm">{a.name}</div>
                    <div className="text-xs text-[var(--text-muted)]">{a.email} · {a.phone || "no phone"} · applied {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => rejectApp(a.id)} data-testid={`reject-application-${a.id}`}
                      className="text-xs font-medium text-[var(--text-muted)] border border-[var(--border-subtle)] rounded-full px-4 py-2 hover:border-[var(--destructive)] hover:text-[var(--destructive)]">
                      Reject
                    </button>
                    <button onClick={() => { setGrantUser({ __appId: a.id, user_id: a.user_id, name: a.name, email: a.email }); setGrant({ rebate_discount_pct: "10", rebate_credit: "0", trade_verified: true }); }}
                      data-testid={`approve-application-${a.id}`} className="btn-gold text-xs font-semibold rounded-full px-4 py-2">
                      Approve & set rebate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="bg-white rounded-2xl border border-[var(--border-subtle)] overflow-hidden">
              {users.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)] last:border-0" data-testid={`admin-user-${u.user_id}`}>
                  <div>
                    <div className="font-medium text-sm">{u.name} <span className="text-xs text-[var(--text-muted)]">· {u.role} · {u.buyer_type}</span></div>
                    <div className="text-xs text-[var(--text-muted)]">{u.email} · {u.phone || "no phone"}</div>
                    {u.trade_verified && <div className="text-xs text-[var(--gold-hover)] font-mono mt-0.5">Trade: {u.rebate_discount_pct}% + {money(u.rebate_credit)}</div>}
                  </div>
                  {(u.buyer_type === "designer" || u.buyer_type === "business") && (
                    <button onClick={() => { setGrantUser(u); setGrant({ rebate_discount_pct: String(u.rebate_discount_pct || ""), rebate_credit: String(u.rebate_credit || ""), trade_verified: true }); }}
                      data-testid={`grant-rebate-${u.user_id}`} className="text-xs font-medium text-[var(--gold)] border border-[var(--border-subtle)] rounded-full px-4 py-2 hover:border-[var(--gold)]">
                      Manage rebate
                    </button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!grantUser} onOpenChange={(o) => !o && setGrantUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif text-2xl">Grant trade rebate</DialogTitle></DialogHeader>
          {grantUser && <p className="text-sm text-[var(--text-muted)]">For {grantUser.name} ({grantUser.email})</p>}
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium">Discount percentage (%)</label>
              <input type="number" min="0" max="100" value={grant.rebate_discount_pct} data-testid="grant-discount-input"
                onChange={(e) => setGrant({ ...grant, rebate_discount_pct: e.target.value })}
                className="mt-1 w-full rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium">Art credit (€ toward free art)</label>
              <input type="number" min="0" value={grant.rebate_credit} data-testid="grant-credit-input"
                onChange={(e) => setGrant({ ...grant, rebate_credit: e.target.value })}
                className="mt-1 w-full rounded-lg border border-[var(--border-subtle)] px-4 py-2.5 text-sm" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={grant.trade_verified} onChange={(e) => setGrant({ ...grant, trade_verified: e.target.checked })} className="accent-[var(--gold)] w-4 h-4" />
              Mark as verified trade member
            </label>
          </div>
          <DialogFooter>
            <button onClick={submitGrant} data-testid="grant-submit-btn" className="btn-gold w-full rounded-full py-3 text-sm font-semibold">Save rebate</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
